import {cacheForceRefresh} from '@github-ui/relay-query-cache/query-helpers'
import {useMemo, useState} from 'react'
import {graphql, useRelayEnvironment, useSubscription} from 'react-relay'
import type {RecordSourceSelectorProxy} from 'relay-runtime'

import type {IssueTimelineViewType} from '../constants/timeline'
import type {
  IssueViewerSubscriptionMetadataSubscription,
  IssueViewerSubscriptionMetadataSubscription$data,
} from './__generated__/IssueViewerSubscriptionMetadataSubscription.graphql'
import type {
  IssueViewerSubscriptionTabbedTimelineSubscription,
  IssueViewerSubscriptionTabbedTimelineSubscription$data,
} from './__generated__/IssueViewerSubscriptionTabbedTimelineSubscription.graphql'
import type {
  IssueViewerSubscriptionTimelineSubscription,
  IssueViewerSubscriptionTimelineSubscription$data,
} from './__generated__/IssueViewerSubscriptionTimelineSubscription.graphql'
import {IssueViewerViewGraphqlQuery} from './IssueViewer'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const metadataSubscription = graphql`
  subscription IssueViewerSubscriptionMetadataSubscription($issueId: ID!) {
    issueUpdated(id: $issueId) {
      issue {
        number
        repository {
          name
          owner {
            login
          }
        }
      }
      deletedCommentId @deleteRecord
      issueMetadataUpdated {
        ...LabelsSectionAssignedLabels
        ...AssigneesSectionAssignees
        ...MilestonesSectionMilestone
        ...ProjectsSectionFragment
        ...DevelopmentSectionFragment
      }
      issueBodyUpdated {
        ...IssueBodyContent
      }
      issueTitleUpdated {
        ...Header
      }
      issueStateUpdated {
        ...HeaderState
        ...IssueActions
      }
      issueTypeUpdated {
        ...HeaderIssueType
        ...TypesSectionTypeFragment
      }
      issueReactionUpdated {
        ...ReactionViewerRelayGroups
      }
      issueFieldsUpdated {
        ...FieldsSectionFieldValues
      }
      commentReactionUpdated {
        ...ReactionViewerRelayGroups
      }
      commentUpdated {
        ...IssueCommentViewerMarkdownViewer
        ...IssueCommentEditorBodyFragment
        issue {
          ...PinnedCommentDisplay
        }
      }
      pinnedCommentUpdated {
        ...IssueCommentHeader
        issue {
          ...PinnedCommentDisplay
        }
      }
      subIssuesUpdated {
        ...SubIssuesList
        ...useHasSubIssues
      }
      issueTransferStateUpdated {
        ...SubIssuesList
        ...useHasSubIssues
        ...IssueBodyViewerSubIssues
      }
      # subIssuesSummaryUpdated: no need to subscribe to this here since we calculate that from the subIssues list directly
      parentIssueUpdated {
        ...RelationshipsSectionFragment
        ...HeaderParentTitle
      }
      issueDependenciesSummaryUpdated {
        ...RelationshipsSectionFragment
        ...HeaderBlockedBySummary
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const timelineSubscription = graphql`
  subscription IssueViewerSubscriptionTimelineSubscription($issueId: ID!, $connections: [ID!]!, $skip: Int) {
    issueUpdated(id: $issueId) {
      issueTimelineUpdated {
        timelineItems(skip: $skip, first: 10, visibleEventsOnly: true) {
          totalCount
          edges @appendEdge(connections: $connections) {
            node {
              __id
              __typename
              ...SubscribedEvent @dangerously_unaliased_fixme
              ...UnsubscribedEvent @dangerously_unaliased_fixme
              ...MentionedEvent @dangerously_unaliased_fixme
              ...IssueComment_issueComment @dangerously_unaliased_fixme
              ...ReactionViewerRelayGroups @dangerously_unaliased_fixme
              ...ClosedEvent @dangerously_unaliased_fixme
              ...ReopenedEvent @dangerously_unaliased_fixme
              ...LockedEvent @dangerously_unaliased_fixme
              ...UnlockedEvent @dangerously_unaliased_fixme
              ...PinnedEvent @dangerously_unaliased_fixme
              ...UnpinnedEvent @dangerously_unaliased_fixme
              ...LabeledEvent @dangerously_unaliased_fixme
              ...RenamedTitleEvent @dangerously_unaliased_fixme
              ...UnlabeledEvent @dangerously_unaliased_fixme
              ...UnassignedEvent @dangerously_unaliased_fixme
              ...AssignedEvent @dangerously_unaliased_fixme
              ...CommentDeletedEvent @dangerously_unaliased_fixme
              ...UserBlockedEvent @dangerously_unaliased_fixme
              ...MilestonedEvent @dangerously_unaliased_fixme
              ...DemilestonedEvent @dangerously_unaliased_fixme
              ...CrossReferencedEvent @dangerously_unaliased_fixme
              ...ReferencedEvent @dangerously_unaliased_fixme
              ...ConnectedEvent @dangerously_unaliased_fixme
              ...TransferredEvent @dangerously_unaliased_fixme
              ...DisconnectedEvent @dangerously_unaliased_fixme
              ...MarkedAsDuplicateEvent @dangerously_unaliased_fixme
              ...UnmarkedAsDuplicateEvent @dangerously_unaliased_fixme
              ...ConvertedToDiscussionEvent @dangerously_unaliased_fixme
              ...AddedToProjectV2Event @dangerously_unaliased_fixme
              ...RemovedFromProjectV2Event @dangerously_unaliased_fixme
              ...ProjectV2ItemStatusChangedEvent @dangerously_unaliased_fixme
              ...ConvertedFromDraftEvent @dangerously_unaliased_fixme
              ...SubIssueAddedEvent @dangerously_unaliased_fixme
              ...SubIssueRemovedEvent @dangerously_unaliased_fixme
              ...ParentIssueAddedEvent @dangerously_unaliased_fixme
              ...ParentIssueRemovedEvent @dangerously_unaliased_fixme
              ...BlockedByAddedEvent @dangerously_unaliased_fixme
              ...BlockedByRemovedEvent @dangerously_unaliased_fixme
              ...BlockingAddedEvent @dangerously_unaliased_fixme
              ...BlockingRemovedEvent @dangerously_unaliased_fixme
              ...IssueTypeAddedEvent @dangerously_unaliased_fixme
              ...IssueTypeRemovedEvent @dangerously_unaliased_fixme
              ...IssueTypeChangedEvent @dangerously_unaliased_fixme
              ...IssueFieldChangedEvent @dangerously_unaliased_fixme
              ...IssueFieldRemovedEvent @dangerously_unaliased_fixme
              ...IssueFieldAddedEvent @dangerously_unaliased_fixme
              ...IssueCommentPinnedEvent @dangerously_unaliased_fixme
              ...IssueCommentUnpinnedEvent @dangerously_unaliased_fixme
            }
          }
        }
      }
    }
  }
`

export const useIssueViewerSubscription = (issueId: string, initialSkip: number | null, connectionId: string) => {
  const relayEnvironment = useRelayEnvironment()
  const [skipCount, setSkipCount] = useState(initialSkip || 0)

  const metadataConfig = useMemo(
    () => ({
      subscription: metadataSubscription,
      variables: {issueId},
      onNext: (resp: IssueViewerSubscriptionMetadataSubscription$data | null | undefined) => {
        if (!resp?.issueUpdated?.issue) return

        // When a comment is deleted, the mutation already handles edge removal
        // and totalCount via its updater. A force refresh here would race with
        // the in-flight mutation, overwriting the base store's totalCount with
        // the server's already-decremented value before the optimistic rollback,
        // causing a double-decrement.
        if (resp.issueUpdated.deletedCommentId) return

        const number = resp.issueUpdated.issue.number
        const repoName = resp.issueUpdated.issue.repository.name
        const repoOwner = resp.issueUpdated.issue.repository.owner.login

        cacheForceRefresh(relayEnvironment, {
          query: IssueViewerViewGraphqlQuery,
          number,
          owner: repoOwner,
          repo: repoName,
        })
      },
    }),
    [issueId, relayEnvironment],
  )

  const timelineConfig = useMemo(
    () => ({
      subscription: timelineSubscription,
      onNext: (resp: IssueViewerSubscriptionTimelineSubscription$data | null | undefined) => {
        if (!resp?.issueUpdated?.issueTimelineUpdated) return

        // Since we only care about events after a certain point, we need to resubscribe with the new skip count if there are newer events
        const newTotalCount = resp.issueUpdated?.issueTimelineUpdated?.timelineItems?.totalCount
        if (newTotalCount != null && newTotalCount > skipCount) {
          setSkipCount(newTotalCount)
        }
      },
      variables: {
        issueId,
        connections: [connectionId],
        skip: skipCount,
      },
    }),
    [issueId, skipCount, connectionId],
  )

  useSubscription<IssueViewerSubscriptionMetadataSubscription>(metadataConfig)
  useSubscription<IssueViewerSubscriptionTimelineSubscription>(timelineConfig)
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const tabbedTimelineSubscription = graphql`
  subscription IssueViewerSubscriptionTabbedTimelineSubscription(
    $issueId: ID!
    $connections: [ID!]!
    $allConnections: [ID!]!
    $skip: Int
    $viewType: TimelineEventViewType
  ) {
    issueUpdated(id: $issueId) {
      deletedCommentId @deleteRecord @deleteEdge(connections: $allConnections)
      issueTimelineUpdated {
        timelineItems(skip: $skip, first: 10, viewType: $viewType) {
          totalCount
          edges @appendEdge(connections: $connections) {
            node {
              __id
              __typename
              ...SubscribedEvent @dangerously_unaliased_fixme
              ...UnsubscribedEvent @dangerously_unaliased_fixme
              ...MentionedEvent @dangerously_unaliased_fixme
              ...IssueComment_issueComment @dangerously_unaliased_fixme
              ...ReactionViewerRelayGroups @dangerously_unaliased_fixme
              ...ClosedEvent @dangerously_unaliased_fixme
              ...ReopenedEvent @dangerously_unaliased_fixme
              ...LockedEvent @dangerously_unaliased_fixme
              ...UnlockedEvent @dangerously_unaliased_fixme
              ...PinnedEvent @dangerously_unaliased_fixme
              ...UnpinnedEvent @dangerously_unaliased_fixme
              ...LabeledEvent @dangerously_unaliased_fixme
              ...RenamedTitleEvent @dangerously_unaliased_fixme
              ...UnlabeledEvent @dangerously_unaliased_fixme
              ...UnassignedEvent @dangerously_unaliased_fixme
              ...AssignedEvent @dangerously_unaliased_fixme
              ...CommentDeletedEvent @dangerously_unaliased_fixme
              ...UserBlockedEvent @dangerously_unaliased_fixme
              ...MilestonedEvent @dangerously_unaliased_fixme
              ...DemilestonedEvent @dangerously_unaliased_fixme
              ...CrossReferencedEvent @dangerously_unaliased_fixme
              ...ReferencedEvent @dangerously_unaliased_fixme
              ...ConnectedEvent @dangerously_unaliased_fixme
              ...TransferredEvent @dangerously_unaliased_fixme
              ...DisconnectedEvent @dangerously_unaliased_fixme
              ...MarkedAsDuplicateEvent @dangerously_unaliased_fixme
              ...UnmarkedAsDuplicateEvent @dangerously_unaliased_fixme
              ...ConvertedToDiscussionEvent @dangerously_unaliased_fixme
              ...AddedToProjectV2Event @dangerously_unaliased_fixme
              ...RemovedFromProjectV2Event @dangerously_unaliased_fixme
              ...ProjectV2ItemStatusChangedEvent @dangerously_unaliased_fixme
              ...ConvertedFromDraftEvent @dangerously_unaliased_fixme
              ...SubIssueAddedEvent @dangerously_unaliased_fixme
              ...SubIssueRemovedEvent @dangerously_unaliased_fixme
              ...ParentIssueAddedEvent @dangerously_unaliased_fixme
              ...ParentIssueRemovedEvent @dangerously_unaliased_fixme
              ...BlockedByAddedEvent @dangerously_unaliased_fixme
              ...BlockedByRemovedEvent @dangerously_unaliased_fixme
              ...BlockingAddedEvent @dangerously_unaliased_fixme
              ...BlockingRemovedEvent @dangerously_unaliased_fixme
              ...IssueTypeAddedEvent @dangerously_unaliased_fixme
              ...IssueTypeRemovedEvent @dangerously_unaliased_fixme
              ...IssueTypeChangedEvent @dangerously_unaliased_fixme
              ...IssueFieldChangedEvent @dangerously_unaliased_fixme
              ...IssueFieldRemovedEvent @dangerously_unaliased_fixme
              ...IssueFieldAddedEvent @dangerously_unaliased_fixme
              ...IssueCommentPinnedEvent @dangerously_unaliased_fixme
              ...IssueCommentUnpinnedEvent @dangerously_unaliased_fixme
            }
          }
        }
      }
    }
  }
`

export const useIssueViewerTabbedSubscription = (
  issueId: string,
  initialSkip: number | null,
  connectionId: string,
  viewType: IssueTimelineViewType,
  frontConnectionId: string,
) => {
  const relayEnvironment = useRelayEnvironment()
  const [skipCount, setSkipCount] = useState(initialSkip || 0)

  const metadataConfig = useMemo(
    () => ({
      subscription: metadataSubscription,
      variables: {issueId},
      onNext: (resp: IssueViewerSubscriptionMetadataSubscription$data | null | undefined) => {
        if (!resp?.issueUpdated?.issue) return

        // Deletion is handled by @deleteEdge + the timeline subscription's
        // updater. A cacheForceRefresh here would race with the in-flight
        // delete mutation, risking a double-decrement of totalCount.
        if (resp.issueUpdated.deletedCommentId) return

        const number = resp.issueUpdated.issue.number
        const repoName = resp.issueUpdated.issue.repository.name
        const repoOwner = resp.issueUpdated.issue.repository.owner.login

        cacheForceRefresh(relayEnvironment, {
          query: IssueViewerViewGraphqlQuery,
          number,
          owner: repoOwner,
          repo: repoName,
        })
      },
    }),
    [issueId, relayEnvironment],
  )

  const allConnectionIds = useMemo(() => [frontConnectionId, connectionId], [frontConnectionId, connectionId])

  const tabbedTimelineConfig = useMemo(
    () => ({
      subscription: tabbedTimelineSubscription,
      onNext: (resp: IssueViewerSubscriptionTabbedTimelineSubscription$data | null | undefined) => {
        const newTotalCount = resp?.issueUpdated?.issueTimelineUpdated?.timelineItems?.totalCount
        if (newTotalCount && newTotalCount > skipCount) {
          setSkipCount(newTotalCount)
        }
      },
      updater: (store: RecordSourceSelectorProxy) => {
        const serverCount = store
          .getRootField('issueUpdated')
          ?.getLinkedRecord('issueTimelineUpdated')
          ?.getLinkedRecord('timelineItems', {skip: skipCount, first: 10, viewType})
          ?.getValue('totalCount') as number | undefined
        const isDeleted = store.getRootField('issueUpdated')?.getValue('deletedCommentId') != null

        // @appendEdge / @deleteEdge handle edges, but Relay doesn't propagate
        // totalCount to our connection records automatically — sync it here.
        for (const connId of allConnectionIds) {
          const conn = store.get(connId)
          if (!conn) continue
          if (serverCount != null) {
            conn.setValue(serverCount, 'totalCount')
          } else if (isDeleted) {
            const prev = Number(conn.getValue('totalCount'))
            if (prev > 0) conn.setValue(prev - 1, 'totalCount')
          }
        }
      },
      variables: {
        issueId,
        connections: [connectionId],
        allConnections: allConnectionIds,
        skip: skipCount,
        viewType,
      },
    }),
    [issueId, skipCount, connectionId, viewType, allConnectionIds],
  )

  useSubscription<IssueViewerSubscriptionMetadataSubscription>(metadataConfig)
  useSubscription<IssueViewerSubscriptionTabbedTimelineSubscription>(tabbedTimelineConfig)
}
